// The sandboxed iframe a report renders in (design §4, §5; security review
// 2026-10-02 point 2).
//
// Agent HTML is untrusted. The document arrives complete from the render
// endpoint (CSP meta first in <head>, base style, bridge script, agent HTML)
// and goes in via `srcdoc`:
//   - always without `allow-same-origin` ⇒ opaque origin: no cookies,
//     localStorage or credentialed access to Goblic's API;
//   - never `allow-popups`, `allow-top-navigation*`, `allow-modals`;
//   - `sandbox=""` (no scripts; the bridge doesn't run either) unless the
//     render says `hasScripts` AND the viewer clicked "Enable interactive
//     content" for this report version (remembered in sessionStorage); only
//     then `sandbox="allow-scripts allow-forms"`. `allow-forms` is needed
//     because without it the browser never fires a form's `submit` event, so
//     an ordinary `<form>` whose handler calls `goblic.submit` would do
//     nothing. A real submission still can't leave: the report's CSP has
//     `form-action 'none'`, and a navigation would trip the load counter.
//
// Navigation: the frame can still navigate itself (a link, a script). We
// count `load` events — the first is the srcdoc, a second means it went
// elsewhere — and the bridge posts `goblic:unload` on `pagehide`. Either way
// the frame is no longer trusted: every later message is ignored, the frame
// is reset (new key, srcdoc again, interactive content off) and a warning
// shows. A generation counter drops answers meant for an earlier document.
//
// While trusted, it accepts `goblic:height`, `goblic:submit` and
// `goblic:get-last-submission` — only from its own iframe's window.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { Notice } from '../ui'
import {
  REPORT_BRIDGE_GET_LAST_SUBMISSION_RESULT,
  REPORT_BRIDGE_SUBMIT,
  REPORT_BRIDGE_SUBMIT_RESULT,
  ReportBridgeGetLastSubmissionMessageSchema,
  ReportBridgeHeightMessageSchema,
  ReportBridgeUnloadMessageSchema,
  normalizeSubmissionData,
  type ReportLastSubmission,
  type ReportSubmitResult,
} from './report-types'

/**
 * The envelope of a `goblic:submit` before its data is checked: the data
 * goes through `normalizeSubmissionData` (JSON round trip + the shared
 * schema), so a bad payload gets an error answer instead of being ignored.
 */
const SubmitEnvelopeSchema = z.object({
  type: z.literal(REPORT_BRIDGE_SUBMIT),
  id: z.string().min(1).max(64).optional(),
  data: z.unknown(),
})

const INTERACTIVE_STORAGE_PREFIX = 'goblic:report-interactive:'

function readInteractiveConsent(key: string): boolean {
  try {
    return window.sessionStorage.getItem(INTERACTIVE_STORAGE_PREFIX + key) === '1'
  } catch {
    return false
  }
}

function writeInteractiveConsent(key: string, on: boolean): void {
  try {
    if (on) window.sessionStorage.setItem(INTERACTIVE_STORAGE_PREFIX + key, '1')
    else window.sessionStorage.removeItem(INTERACTIVE_STORAGE_PREFIX + key)
  } catch {
    // Storage blocked: consent lasts for this view only.
  }
}

export interface ReportFrameProps {
  /** Complete render document from `GET /reports/:id/render` (`html`). */
  html: string
  /** Accessible name of the frame, e.g. the report title. */
  title: string
  /** `hasScripts` from the render: only then is interactive content offered. */
  hasScripts: boolean
  /** Identifies the report version (e.g. `<reportId>:<version>`); consent is remembered per key. */
  consentKey: string
  /**
   * The report called `goblic.submit(data)`. `data` is already validated and
   * normalized to plain JSON. Resolve once the user confirmed (or cancelled)
   * and the API answered; the result is passed back to the report.
   */
  onSubmitRequest: (data: unknown) => Promise<ReportSubmitResult>
  /** The report called `goblic.getLastSubmission()`: the viewer's own latest submission, or null. */
  onLastSubmissionRequest?: () => Promise<ReportLastSubmission | null>
  /** The frame navigated away and was reset (cancel anything it asked for). */
  onReset?: () => void
  /** Height bounds in px; above `maxHeight` the frame scrolls itself. */
  minHeight?: number
  maxHeight?: number
  /**
   * Height while static (no bridge, so no measured height): the report
   * scrolls inside the frame. Defaults to `max(minHeight, 640)`.
   */
  staticHeight?: number | string
  className?: string
}

export function ReportFrame({
  html,
  title,
  hasScripts,
  consentKey,
  onSubmitRequest,
  onLastSubmissionRequest,
  onReset,
  minHeight = 240,
  maxHeight = 4000,
  staticHeight,
  className = '',
}: ReportFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(minHeight)
  const [consented, setConsented] = useState(() => readInteractiveConsent(consentKey))
  const [frameKey, setFrameKey] = useState(0)
  const [navigatedAway, setNavigatedAway] = useState(false)
  const interactive = hasScripts && consented

  // One submission at a time (§5). A ref, not state: the check must hold for
  // two messages arriving in the same tick.
  const pendingRef = useRef(false)
  // Bumped per document (new html, consent change, reset), so an answer for
  // a previous document is dropped instead of being delivered to whatever
  // replaced it.
  const generationRef = useRef(0)
  // `load` events of the current iframe element: 1 = the srcdoc document.
  const loadsRef = useRef(0)
  // Set once the frame showed something other than the original document.
  const untrustedRef = useRef(false)
  const onSubmitRef = useRef(onSubmitRequest)
  onSubmitRef.current = onSubmitRequest
  const onLastRef = useRef(onLastSubmissionRequest)
  onLastRef.current = onLastSubmissionRequest
  const onResetRef = useRef(onReset)
  onResetRef.current = onReset
  const consentKeyRef = useRef(consentKey)
  consentKeyRef.current = consentKey

  // Another version / report: its own consent, a fresh document.
  useEffect(() => {
    setConsented(readInteractiveConsent(consentKey))
    setNavigatedAway(false)
  }, [consentKey])

  // Every new document (html, mode or reset) starts trusted, at minHeight.
  // A layout effect: it must run before the new srcdoc's `load` event.
  useLayoutEffect(() => {
    generationRef.current += 1
    loadsRef.current = 0
    untrustedRef.current = false
    pendingRef.current = false
    setHeight(minHeight)
  }, [html, minHeight, interactive, frameKey])

  /** The frame left its srcdoc: stop listening to it and start over, static. */
  const resetFrame = useCallback(() => {
    if (untrustedRef.current) return
    untrustedRef.current = true
    generationRef.current += 1
    pendingRef.current = false
    // Back to static until the viewer enables it again: a report that keeps
    // navigating can't loop, and the viewer sees what happened first.
    setConsented(false)
    writeInteractiveConsent(consentKeyRef.current, false)
    setNavigatedAway(true)
    setFrameKey((key) => key + 1)
    onResetRef.current?.()
  }, [])

  const trusted = useCallback(
    (generation: number) =>
      generation === generationRef.current && !untrustedRef.current && loadsRef.current <= 1,
    []
  )

  const post = useCallback((message: unknown) => {
    // targetOrigin must be '*': a sandboxed frame without allow-same-origin
    // has an opaque origin ("null"), which no specific targetOrigin matches.
    // We post to one specific window — our own iframe's contentWindow — and
    // only while it still shows the original document (`trusted`).
    iframeRef.current?.contentWindow?.postMessage(message, '*')
  }, [])

  const replySubmit = useCallback(
    (result: ReportSubmitResult, id?: string) => {
      post({
        type: REPORT_BRIDGE_SUBMIT_RESULT,
        ...(id ? { id } : {}),
        ...(result.ok ? { ok: true } : { ok: false, error: result.error.slice(0, 500) }),
      })
    },
    [post]
  )

  useEffect(() => {
    if (!interactive) return // no scripts, no bridge: nothing to listen to
    function onMessage(event: MessageEvent): void {
      const frameWindow = iframeRef.current?.contentWindow
      // Only messages from our own iframe. Origin can't be checked (it's
      // "null" for every sandboxed frame), the window identity can.
      if (!frameWindow || event.source !== frameWindow) return
      const generation = generationRef.current
      if (!trusted(generation)) return

      if (ReportBridgeUnloadMessageSchema.safeParse(event.data).success) {
        resetFrame()
        return
      }

      const heightMessage = ReportBridgeHeightMessageSchema.safeParse(event.data)
      if (heightMessage.success) {
        const next = Math.min(maxHeight, Math.max(minHeight, Math.ceil(heightMessage.data.height)))
        setHeight((current) => (current === next ? current : next))
        return
      }

      const lastMessage = ReportBridgeGetLastSubmissionMessageSchema.safeParse(event.data)
      if (lastMessage.success) {
        const { id } = lastMessage.data
        const ask = onLastRef.current
        const answer = ask ? ask() : Promise.resolve(null)
        answer
          .then(
            (submission) => ({ type: REPORT_BRIDGE_GET_LAST_SUBMISSION_RESULT, id, ok: true as const, submission }),
            () => ({ type: REPORT_BRIDGE_GET_LAST_SUBMISSION_RESULT, id, ok: false as const, error: 'Couldn’t load your last submission.' })
          )
          .then((message) => {
            // Only to the original document (load count still 1).
            if (trusted(generation)) post(message)
          })
        return
      }

      const submitMessage = SubmitEnvelopeSchema.safeParse(event.data)
      if (!submitMessage.success) return
      // Echo the submit's id (contract §7), so the bridge script can match it.
      const id = submitMessage.data.id

      if (pendingRef.current) {
        replySubmit({ ok: false, error: 'Another submission is still waiting for confirmation.' }, id)
        return
      }
      const normalized = normalizeSubmissionData(submitMessage.data.data)
      if (!normalized.ok) {
        replySubmit({ ok: false, error: normalized.error }, id)
        return
      }

      pendingRef.current = true
      onSubmitRef
        .current(normalized.data)
        .catch(
          (error: unknown): ReportSubmitResult => ({
            ok: false,
            error: error instanceof Error ? error.message : String(error),
          })
        )
        .then((result) => {
          if (generation !== generationRef.current) return
          pendingRef.current = false
          if (trusted(generation)) replySubmit(result, id)
        })
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [interactive, minHeight, maxHeight, post, replySubmit, resetFrame, trusted])

  function onLoad(): void {
    loadsRef.current += 1
    // The first load is the srcdoc; any further one is another document.
    if (loadsRef.current > 1) resetFrame()
  }

  function enableInteractive(): void {
    writeInteractiveConsent(consentKey, true)
    setNavigatedAway(false)
    setConsented(true)
  }

  function disableInteractive(): void {
    writeInteractiveConsent(consentKey, false)
    setConsented(false)
  }

  const frameHeight: number | string = interactive
    ? height
    : (staticHeight ?? Math.min(maxHeight, Math.max(minHeight, 640)))

  return (
    <div>
      {navigatedAway ? (
        <div className="border-b border-line-strong p-2">
          <Notice tone="error" onDismiss={() => setNavigatedAway(false)}>
            This report tried to open another page and was reset.
          </Notice>
        </div>
      ) : null}
      {hasScripts ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line-strong bg-signal-soft px-4 py-2.5 text-sm text-ink">
          {interactive ? (
            <>
              <span>Interactive content is on for this version. It was written by an agent.</span>
              <button type="button" className="btn-ghost btn-sm" onClick={disableInteractive}>
                Turn off
              </button>
            </>
          ) : (
            <>
              <span>
                This report contains interactive content written by an agent. Interactive content can send what you
                type to other websites.
              </span>
              <button type="button" className="btn-secondary btn-sm" onClick={enableInteractive}>
                Enable interactive content
              </button>
            </>
          )}
        </div>
      ) : null}
      <iframe
        // A new element per document and mode: the sandbox flags and the load
        // count belong to one srcdoc, never to a page it navigated to.
        key={`${frameKey}:${interactive ? 'interactive' : 'static'}`}
        ref={iframeRef}
        title={title}
        srcDoc={html}
        sandbox={interactive ? 'allow-scripts allow-forms' : ''}
        referrerPolicy="no-referrer"
        onLoad={onLoad}
        // No powerful features for the report, whatever the browser defaults.
        allow="camera 'none'; microphone 'none'; geolocation 'none'; clipboard-read 'none'; clipboard-write 'none'; payment 'none'; usb 'none'; fullscreen 'none'"
        style={{ height: frameHeight }}
        className={`block w-full border-0 bg-surface ${className}`}
      />
    </div>
  )
}
